import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FileText, Loader2 } from 'lucide-react';
import { Modal, Button, InputField } from '@shared/components';
import { useDarkMode } from '../context/DarkModeContext';
import { getUser, updateUser } from '../state/authSlice';
import { getCourierSetting } from '../state/courierSlice';
import { CourierUserApi } from '@shared/sdk-v1';
import { apiConfig } from '../shared/api-config';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import { validateTrn, formatTrn } from '../utils/trnValidation';

const TRN_DISMISSED_KEY = 'trn_requirement_dismissed';

/**
 * Modal to prompt existing users to provide their TRN when requireTrnOnSignup is enabled
 * Shows for authenticated users who don't have a TRN set
 */
const TrnRequirementModal: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const courierSetting = useSelector(getCourierSetting);

  const [isOpen, setIsOpen] = useState(false);
  const [trnNumber, setTrnNumber] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const requireTrnOnSignup = courierSetting?.requireTrnOnSignup ?? false;

  // Check if we should show the modal
  useEffect(() => {
    if (user && !user.trnNumber && requireTrnOnSignup) {
      // When TRN is required, always show the modal (ignore dismissal)
      // Users must provide TRN to continue
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [user, requireTrnOnSignup]);

  // Clear dismissal flag if user adds TRN
  useEffect(() => {
    if (user?.trnNumber && user?.id) {
      const dismissedKey = `${TRN_DISMISSED_KEY}_${user.id}`;
      localStorage.removeItem(dismissedKey);
    }
  }, [user?.trnNumber, user?.id]);

  const handleTrnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTrnNumber(formatTrn(e.target.value));
    setError('');
  };

  const handleSave = async () => {
    // Validate TRN
    const validation = validateTrn(trnNumber);
    if (!validation.valid) {
      setError(validation.error || 'Please enter a valid TRN');
      return;
    }

    if (!user?.id) return;

    setIsLoading(true);
    setError('');

    try {
      const courierUserApi = new CourierUserApi(apiConfig());
      const rawTrn = trnNumber.replace(/\D/g, '');

      await courierUserApi.courierUserPrototypePatchAttributes({
        id: user.id.toString(),
        data: { trnNumber: rawTrn },
      });

      // Update Redux store
      dispatch(updateUser({ ...user, trnNumber: rawTrn }));

      showSuccessToast({
        title: 'Success',
        message: 'TRN saved successfully',
      });

      setIsOpen(false);
      setTrnNumber('');
    } catch (err) {
      console.error('Failed to save TRN:', err);
      showErrorToast({
        title: 'Error',
        message: 'Failed to save TRN. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    // Don't allow skipping if TRN is required
    if (requireTrnOnSignup) return;

    if (user?.id) {
      const dismissedKey = `${TRN_DISMISSED_KEY}_${user.id}`;
      localStorage.setItem(dismissedKey, 'true');
    }
    setIsOpen(false);
    setTrnNumber('');
    setError('');
  };

  // When TRN is required, don't allow closing the modal
  const handleClose = () => {
    if (requireTrnOnSignup) return;
    handleSkip();
  };

  const isValid = validateTrn(trnNumber).valid;

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="TRN Required"
      size="small"
      hideCloseButton={requireTrnOnSignup}
    >
      <div className="flex flex-col items-center">
        {/* Icon */}
        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>

        {/* Description */}
        <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
          Please provide your Tax Registration Number (TRN) to continue using our services.
        </p>

        {/* TRN Input */}
        <div className="w-full mb-4">
          <InputField
            label="TRN"
            name="trnNumber"
            type="text"
            value={trnNumber}
            onChange={handleTrnChange}
            placeholder="XXX-XXX-XXX"
            fullWidth
            darkMode={isDarkMode}
            className={error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
            icon={<FileText className="h-5 w-5 text-gray-400" />}
          />
          {error && (
            <p className="text-sm text-red-600 mt-1">{error}</p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Enter your 9-digit Tax Registration Number
          </p>
        </div>

        {/* Buttons */}
        <div className="w-full space-y-2">
          <Button
            variant="primary"
            fullWidth
            onClick={handleSave}
            disabled={!isValid || isLoading}
            className="flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save TRN'
            )}
          </Button>
          {!requireTrnOnSignup && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={isLoading}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TrnRequirementModal;
